import { Listing } from '../../../types';
import { SearchFilters } from '../../repository';
import { isSpaceListing } from './predicates';
import { calculateDistance } from '../../../utils/shipping';
import { allowsExternalCatering, allowsCorkage } from '../../../utils/catering.mjs';

function normalizeText(str?: string): string {
    return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/**
 * Filters a list of listings based on the provided search filters.
 */
export function filterListings(listings: Listing[], filters?: SearchFilters): Listing[] {
    let result = listings.filter(isSpaceListing);

    if (!filters) return result;

    return result.filter(l => {
        const center = filters.center;
        const listingLat = Number(l.spaceDetails?.address?.lat);
        const listingLng = Number(l.spaceDetails?.address?.lon);
        const hasListingCoordinates = Number.isFinite(listingLat) && Number.isFinite(listingLng);

        // Category
        if (filters.category && filters.category !== 'all' && l.category !== filters.category) return false;

        // Transaction Type
        if (filters.transactionType && l.transactionType !== filters.transactionType) return false;

        // Query (Title, Description, Location, City) - Multi-keyword support
        if (filters.query) {
            const keywords = normalizeText(filters.query).split(/\s+/).filter(Boolean);
            const title = normalizeText(l.title);
            const desc = normalizeText(l.description);
            const loc = normalizeText(l.location);
            const city = normalizeText(l.spaceDetails?.address?.city);

            // Every keyword must be present in at least one of the fields
            const allKeywordsMatch = keywords.every(kw =>
                title.includes(kw) || desc.includes(kw) || loc.includes(kw) || city.includes(kw)
            );

            if (!allKeywordsMatch) return false;
        }

        // Location (Specific field)
        if (filters.location) {
            const loc = normalizeText(filters.location);
            const listingLoc = normalizeText(l.location);
            const listingCity = normalizeText(l.spaceDetails?.address?.city);

            // Once the searched place has been geocoded, the radius — not the
            // city-name substring — defines the search area. Requiring both
            // made every neighbouring town disappear from radius searches.
            // Listings without coordinates retain the conservative text
            // fallback instead of being included at an unknown distance.
            if (center && filters.radius && hasListingCoordinates) {
                const distance = calculateDistance(center, { lat: listingLat, lng: listingLng });
                if (distance > filters.radius) return false;
            } else if (!listingLoc.includes(loc) && !listingCity.includes(loc)) {
                return false;
            }
        }

        // Price
        const price = l.spaceDetails?.pricePerHour || l.price || 0;
        if (filters.priceMin !== undefined && price < filters.priceMin) return false;
        if (filters.priceMax !== undefined && price > filters.priceMax) return false;

        // Capacity
        if (filters.capacity !== undefined) {
            const cap = l.spaceDetails?.maxGuests || 0;
            if (cap < filters.capacity) return false;
        }

        // Rating
        if (filters.minRating !== undefined) {
            const rating = l.spaceDetails?.googleRating || 0;
            if (rating < filters.minRating) return false;
        }

        // Parking
        if (filters.parking) {
            if (!l.spaceDetails?.parkingAvailable) return false;
        }

        // Catering & Korkovné
        if (filters.ownCateringAllowed && !allowsExternalCatering(l)) {
            return false;
        }

        if (filters.corkageAllowed && !allowsCorkage(l)) {
            return false;
        }

        // Amenities
        if (filters.amenities && filters.amenities.length > 0) {
            const listingAmenities = l.spaceDetails?.amenities || [];
            const hasAll = filters.amenities.every(a => listingAmenities.includes(a));
            if (!hasAll) return false;
        }

        // Event Types
        if (filters.eventTypes && filters.eventTypes.length > 0) {
            const listingEventTypes = l.spaceDetails?.eventTypes || [];
            const hasAny = filters.eventTypes.some(t => listingEventTypes.includes(t));
            if (!hasAny) return false;
        }

        // Date & Time Availability (Weekly Schedule)
        if (filters.date) {
            const date = new Date(filters.date);
            if (!isNaN(date.getTime())) {
                const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon...
                const schedule = l.spaceDetails?.weeklySchedule;

                if (schedule?.days) {
                    const dayConfig = schedule.days.find(d => d.dayOfWeek === dayOfWeek);
                    if (!dayConfig || !dayConfig.enabled) return false;

                    // Time check
                    if (filters.timeFrom && filters.timeFrom !== 'all' && filters.timeTo && filters.timeTo !== 'all') {
                        if (dayConfig.from && dayConfig.to) {
                            if (filters.timeFrom < dayConfig.from || filters.timeTo > dayConfig.to) {
                                return false;
                            }
                        }
                    }
                }
            }
        }

        // Technical Specs
        if (filters.minArea !== undefined) {
            const area = l.spaceDetails?.areaSquareMeters || 0;
            if (area < filters.minArea) return false;
        }

        if (filters.seatingTheater !== undefined) {
            const cap = l.spaceDetails?.seatingCapacities?.theater || 0;
            if (cap < filters.seatingTheater) return false;
        }

        if (filters.seatingSchool !== undefined) {
            const cap = l.spaceDetails?.seatingCapacities?.school || 0;
            if (cap < filters.seatingSchool) return false;
        }

        if (filters.seatingBanquet !== undefined) {
            const cap = l.spaceDetails?.seatingCapacities?.banquet || 0;
            if (cap < filters.seatingBanquet) return false;
        }

        if (filters.seatingCocktail !== undefined) {
            const cap = l.spaceDetails?.seatingCapacities?.cocktail || 0;
            if (cap < filters.seatingCocktail) return false;
        }

        return true;
    });
}
